import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Empty, Field, Icon, Panel, type IconName } from '../design/index.js'
import { useApi, type SearchHit } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths } from '../app/router.js'
import { useDocumentMenu } from '../framework/queries.js'
import { ErrorBlock, Loading, NoticeBlock } from '../framework/feedback.js'
import { HUB_ICON, favouritePath, useFavouriteMutations } from '../framework/favourites.js'

/** Where one hit opens. A record opens itself; a screen opens itself. */
const hitPath = (companyId: string, hit: SearchHit): string => {
  switch (hit.kind) {
    case 'document': return paths.document(companyId, hit.route, hit.id)
    case 'master': return paths.masterRecord(companyId, hit.route, hit.id)
    case 'type': return paths.list(companyId, hit.route)
    case 'masterList': return paths.masterList(companyId, hit.route)
    case 'report': return paths.report(companyId, hit.route)
    case 'dashboard': return paths.dashboard(companyId, hit.route)
    case 'page': return hit.route === 'drive' ? paths.drive(companyId) : paths.home(companyId)
  }
}

interface MenuEntry { readonly group: string; readonly label: string; readonly path: string }

/**
 * Every screen on the menu, for the search box to launch (HOME-004).
 *
 * The reference's "Type to search" drops the whole menu the moment it is
 * clicked — every transaction, master, report and dashboard — and narrows it
 * as you type. The four menus are the same cached reads the hubs make.
 */
const useMenuEntries = (companyId: string): readonly MenuEntry[] => {
  const api = useApi()
  const docs = useDocumentMenu()
  const masters = useQuery({ queryKey: ['masterDefs'], queryFn: () => api.masterMenu(), staleTime: 10 * 60_000 })
  const reports = useQuery({ queryKey: ['reportDefs'], queryFn: () => api.reportMenu(), staleTime: 10 * 60_000 })
  const dashboards = useQuery({ queryKey: ['dashboardDefs'], queryFn: () => api.dashboardMenu(), staleTime: 10 * 60_000 })
  return useMemo(() => [
    ...(docs.data ?? []).filter((d) => d.hidden !== true)
      .map((d) => ({ group: 'Transactions', label: d.labelPlural, path: paths.list(companyId, d.route) })),
    { group: 'Transactions', label: 'Project Drive', path: paths.drive(companyId) },
    ...(masters.data ?? []).map((d) => ({ group: 'Masters', label: d.labelPlural, path: paths.masterList(companyId, d.route) })),
    ...(reports.data ?? []).map((d) => ({ group: 'Reports', label: d.label, path: paths.report(companyId, d.route) })),
    ...(dashboards.data ?? []).map((d) => ({ group: 'Dashboards', label: d.label, path: paths.dashboard(companyId, d.route) })),
  ], [companyId, docs.data, masters.data, reports.data, dashboards.data])
}

const TODAY = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

interface StatProps {
  readonly to: string
  readonly icon: IconName
  readonly title: string
  readonly caption: string
  readonly value: number
  readonly chip: string
  readonly tone: 'good' | 'warn' | 'bad' | 'none'
}

const Stat = ({ to, icon, title, caption, value, chip, tone }: StatProps) => (
  <Link to={to} className="stat">
    <span className="stat__head">
      <span className="stat__icon"><Icon name={icon} size={18} /></span>
      <span className="stat__title">{title}</span>
    </span>
    <span className="stat__caption">{caption}</span>
    <span className="stat__figure">{value > 999 ? '999+' : value}</span>
    <span className={tone === 'none' ? 'stat__chip' : `stat__chip stat__chip--${tone}`}>{chip}</span>
  </Link>
)

export const Home = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const home = useQuery({ queryKey: ['home', companyId], queryFn: () => api.home() })
  // The same query the sidebar badges use, so this is a cache hit, not a call.
  const inbox = useQuery({
    queryKey: ['inboxCounts', companyId],
    queryFn: () => api.inboxCounts(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const [term, setTerm] = useState('')
  const [needle, setNeedle] = useState('')
  const [open, setOpen] = useState(false)
  const entries = useMenuEntries(companyId)
  const { toggle: unstar, move } = useFavouriteMutations()

  /*
    Records, not only the names of screens.

    The box used to filter the document-type menu in the browser, so typing a
    project number or an invoice number found nothing at all — while the label
    said "Type to search" and docs/01 describes a global menu-and-entity
    search. The server answers now, over what this person is allowed to see.

    Debounced, because this is a keystroke-by-keystroke box and each answer is
    a handful of queries.
  */
  useEffect(() => {
    const trimmed = term.trim()
    const timer = setTimeout(() => setNeedle(trimmed), 200)
    return () => clearTimeout(timer)
  }, [term])

  const search = useQuery({
    queryKey: ['search', companyId, needle],
    queryFn: () => api.search(needle),
    enabled: needle.length >= 2,
    staleTime: 30_000,
  })

  /*
    Records first, in the order the server sent them, then every screen on the
    menu whose name matches — all of them when nothing is typed (HOME-004).
    The server's own "Go to" hits are the same screens, so they are left out
    rather than listed twice.
  */
  const groups = useMemo(() => {
    const out = new Map<string, Array<{ key: string; label: string; secondary: string | null; path: string }>>()
    const push = (group: string, row: { key: string; label: string; secondary: string | null; path: string }): void => {
      const list = out.get(group)
      if (list) list.push(row)
      else out.set(group, [row])
    }
    if (needle.length >= 2) {
      for (const hit of search.data ?? []) {
        if (hit.group === 'Go to') continue
        push(hit.group, { key: `${hit.kind}:${hit.id}`, label: hit.label, secondary: hit.secondary, path: hitPath(companyId, hit) })
      }
    }
    const typed = term.trim().toLowerCase()
    for (const e of entries) {
      if (typed !== '' && !e.label.toLowerCase().includes(typed)) continue
      push(e.group, { key: e.path, label: e.label, secondary: null, path: e.path })
    }
    return [...out.entries()]
  }, [search.data, needle, term, entries, companyId])

  if (home.isLoading) return <div className="page"><Loading /></div>
  if (home.error) return <div className="page"><ErrorBlock error={home.error} /></div>

  const payload = home.data
  const unread = payload?.unreadNotifications ?? 0
  const counts = inbox.data ?? { approvals: 0, tasks: 0, alerts: 0 }
  const favourites = payload?.favourites ?? []
  const now = new Date()

  return (
    <div className="page">
      <div className="doc-head">
        <div>
          {/*
            The reference's greeting: `Hello <name> 👋` with the unread count
            beside it as a link to the notifications (99+ past ninety-nine).
          */}
          <h1 className="greeting">
            Hello {payload?.greetingName ?? 'there'} <span aria-hidden>👋</span>
            {Math.max(counts.alerts, unread) > 0 ? (
              <Link to={paths.hub(companyId, 'alerts')} className="greeting-unread">
                {`${Math.max(counts.alerts, unread) > 99 ? '99+' : Math.max(counts.alerts, unread)} unread notification${Math.max(counts.alerts, unread) === 1 ? '' : 's'}`}
              </Link>
            ) : null}
          </h1>
          <p className="greeting-date">
            {payload?.company.name ?? 'This company'} · {TODAY.format(now)}
          </p>
        </div>
        <div className="doc-actions">
          <Link to={paths.hub(companyId, 'reports')} className="btn btn--ghost">
            <span className="btn__icon"><Icon name="chart" size={18} /></span>
            Reports
          </Link>
          <Link to={paths.hub(companyId, 'transactions')} className="btn btn--primary">
            <span className="btn__icon"><Icon name="plus" size={18} /></span>
            New document
          </Link>
        </div>
      </div>

      <div className="stat-grid">
        <Stat
          to={paths.hub(companyId, 'approvals')}
          icon="check"
          title="Approvals"
          caption="Waiting for your signature"
          value={counts.approvals}
          chip={counts.approvals > 0 ? 'Waiting for you' : 'Nothing waiting'}
          tone={counts.approvals > 0 ? 'warn' : 'good'}
        />
        <Stat
          to={paths.hub(companyId, 'tasks')}
          icon="clip"
          title="Tasks"
          caption="Work assigned to you"
          value={counts.tasks}
          chip={counts.tasks > 0 ? 'Open' : 'All done'}
          tone={counts.tasks > 0 ? 'none' : 'good'}
        />
        <Stat
          to={paths.hub(companyId, 'alerts')}
          icon="bell"
          title="Alerts"
          caption="Notifications you have not read"
          value={Math.max(counts.alerts, unread)}
          chip={Math.max(counts.alerts, unread) > 0 ? 'Unread' : 'All read'}
          tone={Math.max(counts.alerts, unread) > 0 ? 'bad' : 'good'}
        />
        <Stat
          to={paths.hub(companyId, 'favourites')}
          icon="star"
          title="Starred"
          caption="Screens you keep close"
          value={favourites.length}
          chip={favourites.length > 0 ? 'Shortcuts below' : 'Star a screen to add one'}
          tone="none"
        />
      </div>

      <div className="home-grid">
        <Panel title="Search">
          <div
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false) }}
          >
            <Field
              label="Type to search"
              hint="A document number, a customer, a supplier, a material — or the name of any screen."
            >
              <input
                className="control"
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls="home-launcher"
                autoComplete="off"
                value={term}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}
                onChange={(e) => { setTerm(e.currentTarget.value); setOpen(true) }}
              />
            </Field>
            {open && groups.length > 0 ? (
              <div className="search-results search-results--launcher" id="home-launcher" role="listbox">
                {groups.map(([group, rows]) => (
                  <div key={group}>
                    <p className="search-results__heading">{group}</p>
                    {rows.map((row) => (
                      <button
                        key={row.key}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="search-result"
                        onClick={() => navigate(row.path)}
                      >
                        {row.label}
                        {row.secondary ? <span className="search-result__group"> · {row.secondary}</span> : null}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : open && term.trim() !== '' && !search.isFetching ? (
              <p className="helper" style={{ marginTop: 12 }}>No results found.</p>
            ) : null}
          </div>
        </Panel>

        <Panel title="Starred shortcuts" aside={favourites.length > 0 ? `${favourites.length}` : undefined}>
          <p className="helper" style={{ marginBottom: 12 }}>
            You can add menu items as shortcuts below. Set them in{' '}
            <Link to={paths.hub(companyId, 'transactions')}>Transactions</Link>,{' '}
            <Link to={paths.hub(companyId, 'reports')}>Reports</Link> or{' '}
            <Link to={paths.hub(companyId, 'masters')}>Masters</Link>.
          </p>
          {favourites.length === 0 ? (
            <Empty
              title="Nothing starred yet."
              body="Star an item on the Transactions, Reports or Masters page and it will be listed here."
            />
          ) : (
            <div>
              {/*
                Reordered and removed from here, as the reference does it: the
                arrows and the cross appear on the row being pointed at
                (HOME-006).
              */}
              {favourites.map((f, i) => (
                <div key={f.key} className="shortcut-row">
                  <Link to={favouritePath(companyId, f)} className="shortcut">
                    <span className="shortcut__icon" title={f.hub}><Icon name={HUB_ICON[f.hub] ?? 'doc'} size={18} /></span>
                    <span>{f.label}</span>
                    <span className="helper" style={{ marginLeft: 'auto' }}>{f.hub}</span>
                  </Link>
                  <span className="shortcut-row__actions">
                    <button type="button" className="icon-button" aria-label={`Move ${f.label} down`}
                      disabled={i === favourites.length - 1 || move.isPending}
                      onClick={() => move.mutate({ key: f.key, direction: 'down' })}>
                      <Icon name="down" size={18} />
                    </button>
                    <button type="button" className="icon-button" aria-label={`Move ${f.label} up`}
                      disabled={i === 0 || move.isPending}
                      onClick={() => move.mutate({ key: f.key, direction: 'up' })}>
                      <Icon name="up" size={18} />
                    </button>
                    <button type="button" className="icon-button" aria-label={`Remove ${f.label} from shortcuts`}
                      disabled={unstar.isPending}
                      onClick={() => unstar.mutate({ key: f.key, on: false })}>
                      <Icon name="x" size={18} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  )
}
